import { QueryFunctionContext, useQuery, UseQueryResult } from '@tanstack/react-query';
import { useEffect } from 'react';

import { endpoints } from 'global/endpoints';
import http from 'utils/http';
import toaster from 'utils/toaster';

type GeolocationPayload = {
  start_latitude?: number;
  start_longitude?: number;
  end_latitude?: number;
  end_longitude?: number;
};

type GeolocationResponse = {
  payload: GeolocationPayload;
};

const fetchGeolocationsByTimesheetId = (timesheetId: string, signal: any): Promise<GeolocationResponse> => {
  return http().get(endpoints.whoIsWorking.geoLocations.replace('{timesheetId}', timesheetId), { signal });
};

export function useFetchGeolocationsByTimesheetId(
  timesheetId: string,
  options?: any
): UseQueryResult<GeolocationResponse, unknown> {
  const queryResult = useQuery<GeolocationResponse, unknown>({
    queryKey: ['fetchGeolocationsByTimesheetId', timesheetId],
    queryFn: ({ signal }: QueryFunctionContext) => fetchGeolocationsByTimesheetId(timesheetId, signal),
    refetchInterval: 5000 * 60,
    cacheTime: 100,
    staleTime: 100,
    ...options,
    enabled: timesheetId ? true : false,
  });

  // Handle side effects when query is settled (success or error)
  useEffect(() => {
    if (queryResult.isSuccess || queryResult.isError) {
      const data = queryResult.data;
      if (
        !(data?.payload?.start_latitude && data?.payload?.start_longitude) &&
        !(data?.payload?.end_latitude && data?.payload?.end_longitude)
      ) {
        toaster.error({ description: 'No geo coordinates are available to show on map' });
      }
    }
  }, [queryResult.isSuccess, queryResult.isError, queryResult.data]);

  return queryResult;
}
